<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="outreach-contact-top-reaction">
    <BaseOutreachContactStat
      v-if="!topReaction"
      :tooltip="
        getLocaleText('outreach_contact_top_reaction.no_top_reaction_tooltip')
      "
    >
      <div class="outreach-contact-top-reaction__no-value">
        {{ topReactionText }}
      </div>
    </BaseOutreachContactStat>

    <BaseButtonDropdown
      v-else
      interaction="hover"
      new-design
      new-dropdown
    >
      <template #btn="{ listeners }">
        <div
          class="outreach-contact-top-reaction__container"
          v-on="listeners"
        >
          <BaseOutreachContactStat>
            <div class="outreach-contact-top-reaction__container-emoji">
              {{ topReactionEmoji }}
            </div>

            {{ topReactionText }}
          </BaseOutreachContactStat>
        </div>
      </template>
      <div class="outreach-contact-top-reaction__stats">
        <div class="outreach-contact-top-reaction__stats-title">
          {{ getLocaleText("outreach_contact_top_reaction.top_reaction") }}
        </div>

        <div
          class="outreach-contact-top-reaction__stats-description"
          v-html="reactionsDescription"
        />

        <div class="outreach-contact-top-reaction__stats-row">
          <div
            v-for="reaction in reactions"
            :key="reaction.id"
            class="outreach-contact-top-reaction__stat"
          >
            <div class="outreach-contact-top-reaction__stat-emoji">
              {{ reaction.emoji }}
            </div>

            <div class="outreach-contact-top-reaction__stat-label">
              {{ reaction.text }}
            </div>

            <div class="outreach-contact-top-reaction__stat-value">
              {{ reaction.value }}
            </div>
          </div>
        </div>
      </div>
    </BaseButtonDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButtonDropdown } from "shared/components/base";
import BaseOutreachContactStat from "shared/components/Outreach/OutreachContact/BaseOutreachContactStat.vue";
import type { ContactType } from "shared/composables/useContact";
import { prettyNumber } from "shared/helpers/number";
import type { Nullable } from "shared/types";

interface OutreachContactTopReactionProps {
  contact: ContactType;
}

interface ReactionContact {
  dominant_facebook_reaction: Nullable<string>;
  total_angry_reactions: Nullable<number>;
  total_haha_reactions: Nullable<number>;
  total_love_reactions: Nullable<number>;
  total_sad_reactions: Nullable<number>;
  total_wow_reactions: Nullable<number>;
}

const props = defineProps<OutreachContactTopReactionProps>();

const REACTIONS = {
  ANGRY: "angry",
  HAHA: "haha",
  LOVE: "love",
  SAD: "sad",
  WOW: "wow",
} as const;

type ReactionType = (typeof REACTIONS)[keyof typeof REACTIONS];

const REACTION_TYPES: ReactionType[] = [
  REACTIONS.ANGRY,
  REACTIONS.HAHA,
  REACTIONS.LOVE,
  REACTIONS.SAD,
  REACTIONS.WOW,
];

const REACTION_EMOJIS: Record<ReactionType, string> = {
  angry: "😡",
  haha: "😄",
  love: "😍",
  sad: "😢",
  wow: "😲",
};

function hasReactionStats(
  contact: ContactType
): contact is ContactType & ReactionContact {
  return (
    "dominant_facebook_reaction" in contact &&
    "total_angry_reactions" in contact &&
    "total_haha_reactions" in contact &&
    "total_love_reactions" in contact &&
    "total_sad_reactions" in contact &&
    "total_wow_reactions" in contact
  );
}

function getReactionType(reaction: Nullable<string>): ReactionType | null {
  return (
    REACTION_TYPES.find((reactionType) => reactionType === reaction) || null
  );
}

const topReaction = computed<ReactionType | null>(() => {
  if (!hasReactionStats(props.contact)) {
    return null;
  }

  return getReactionType(props.contact.dominant_facebook_reaction);
});

const topReactionEmoji = computed<string>(() => {
  if (!topReaction.value) {
    return "";
  }

  return REACTION_EMOJIS[topReaction.value];
});

function getReactionText(reactionType: ReactionType): string {
  return getLocaleText(`outreach_contact_top_reaction.${reactionType}`);
}

const topReactionText = computed<string>(() =>
  topReaction.value ? getReactionText(topReaction.value) : "--"
);

function getReactionTotal(reactionType: ReactionType): number {
  if (!hasReactionStats(props.contact)) {
    return 0;
  }

  const reactionTotals: Record<ReactionType, Nullable<number>> = {
    angry: props.contact.total_angry_reactions,
    haha: props.contact.total_haha_reactions,
    love: props.contact.total_love_reactions,
    sad: props.contact.total_sad_reactions,
    wow: props.contact.total_wow_reactions,
  };

  return reactionTotals[reactionType] ?? 0;
}

const totalReactions = computed<number>(() => {
  const totals = REACTION_TYPES.map((reactionType) =>
    getReactionTotal(reactionType)
  );

  return totals.reduce<number>((acc, total) => acc + total, 0);
});

const totalReactionsFormatted = computed(() =>
  prettyNumber(totalReactions.value, "suffix")
);

const reactionPercentages = computed<Record<ReactionType, string>>(() => {
  const percentages: Record<ReactionType, string> = {
    angry: "0%",
    haha: "0%",
    love: "0%",
    sad: "0%",
    wow: "0%",
  };

  REACTION_TYPES.forEach((reactionType) => {
    const total = getReactionTotal(reactionType);
    let percentage = 0;

    if (totalReactions.value && total) {
      percentage = Math.round((total / totalReactions.value) * 100);
    }

    percentages[reactionType] = `${percentage}%`;
  });

  return percentages;
});

const reactionsDescription = computed<string>(() =>
  getLocaleText("outreach_contact_top_reaction.description", {
    total: totalReactionsFormatted.value,
    reaction: topReactionText.value,
    percentage: topReaction.value
      ? reactionPercentages.value[topReaction.value]
      : "0%",
  })
);

const reactions = computed<
  Array<{ emoji: string; id: ReactionType; text: string; value: string }>
>(() =>
  REACTION_TYPES.map((reactionType) => ({
    id: reactionType,
    emoji: REACTION_EMOJIS[reactionType],
    text: getReactionText(reactionType),
    value: reactionPercentages.value[reactionType],
  }))
);
</script>

<style lang="scss" scoped>
.outreach-contact-top-reaction {
  font-weight: 500;
  line-height: 150%;

  &__no-value {
    color: var(--s-color-denim-5);
    text-decoration: none;
  }

  &__container {
    :deep(.q-chip__content) {
      gap: var(--s-spacing-sm);
    }

    &-emoji {
      font-size: var(--s-font-size-lg);
    }
  }

  &__stats {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-lg);
    padding: var(--s-spacing-lg);
    max-width: 400px;

    &-title {
      font-weight: var(--s-font-weight-bold);
      line-height: 150%;
      color: var(--s-color-denim-9);
    }

    &-description {
      font-weight: normal;
      line-height: 150%;
      color: var(--s-color-denim-7);

      :deep(strong) {
        color: var(--s-color-denim-9);
      }
    }

    &-row {
      display: flex;
      flex: 1;
      justify-content: space-between;
      gap: 24px;
    }
  }

  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;

    &-emoji {
      font-size: 24px;
    }

    &-label {
      color: var(--s-color-denim-7);
      font-weight: 500;
      line-height: 150%;
      margin-top: 6px;
    }

    &-value {
      margin-top: 8px;
      display: flex;
      width: 55px;
      padding: 4px 16px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 100px;
      border: 1px solid var(--s-color-denim-2);
      background-color: var(--s-color-denim-1);
      font-size: 12px;
      font-weight: 500;
      line-height: 150%;
    }
  }
}
</style>
